import {
  AgeAssuranceAttemptDependencies,
  AgeAssuranceAttemptTrigger,
  AgeAssuranceBlockedReason,
  AgeAssuranceEligibility,
  AgeAssuranceGateState,
  AgeAssuranceNativeResult,
  AgeAssuranceOutcome,
  AgeAssuranceSnapshot,
  PlatformAgeAssuranceResult,
  UserGeo,
} from '../types';

import { classifyAgeRange, hasCompleteJurisdiction, isTexasJurisdiction } from './decision';

type StartupOptions = AgeAssuranceAttemptDependencies & {
  enabled: boolean;
  isWeb: boolean;
};

type RetryOptions = AgeAssuranceAttemptDependencies & {
  enabled: boolean;
};

type StateListener = (snapshot: AgeAssuranceSnapshot) => void;

type AttemptResult = (
  | { state: 'allowed'; blockedReason: null }
  | { state: 'blocked'; blockedReason: AgeAssuranceBlockedReason }
) & {
  outcome: AgeAssuranceOutcome;
  nativeResult: AgeAssuranceNativeResult;
  eligibility: AgeAssuranceEligibility;
};

const allowedResult = (
  outcome: AgeAssuranceOutcome,
  nativeResult: AgeAssuranceNativeResult = 'not_called',
  eligibility: AgeAssuranceEligibility = 'not_checked'
): Extract<AttemptResult, { state: 'allowed' }> => ({
  state: 'allowed' as const,
  blockedReason: null,
  outcome,
  nativeResult,
  eligibility,
});

export const executeAgeAssuranceAttempt = async (
  dependencies: AgeAssuranceAttemptDependencies
): Promise<AttemptResult> => {
  let geo: UserGeo | null;

  try {
    geo = await dependencies.queryUserGeo();
  } catch {
    return allowedResult('geography_unavailable');
  }

  if (!hasCompleteJurisdiction(geo)) {
    return allowedResult('geography_unavailable');
  }

  if (!isTexasJurisdiction(geo)) {
    return allowedResult('outside_texas');
  }

  let platformResult: PlatformAgeAssuranceResult;
  try {
    platformResult = await dependencies.requestPlatformAgeAssurance();
  } catch {
    return allowedResult('native_failure', 'error_unknown');
  }

  const eligibility = platformResult.eligibility ?? 'not_checked';
  if (platformResult.outcome !== 'age_range') {
    if (
      platformResult.outcome === 'age_not_shared' ||
      platformResult.outcome === 'android_access_not_shared'
    ) {
      return {
        state: 'blocked',
        blockedReason: 'age_sharing_required',
        outcome: platformResult.outcome,
        nativeResult: platformResult.nativeResult,
        eligibility,
      };
    }
    return allowedResult(platformResult.outcome, platformResult.nativeResult, eligibility);
  }

  const classification = classifyAgeRange(platformResult);
  if (classification === 'under_13') {
    return {
      state: 'blocked',
      blockedReason: 'under_13',
      outcome: 'age_under_13',
      nativeResult: 'response_under_threshold',
      eligibility,
    };
  }

  if (classification === 'age_13_plus') {
    return allowedResult('age_13_plus', 'response_at_or_above_threshold', eligibility);
  }

  return allowedResult(
    'native_unavailable',
    classification === 'invalid' ? 'response_invalid' : 'response_ambiguous',
    eligibility
  );
};

export class AgeAssuranceCoordinator {
  private snapshot: AgeAssuranceSnapshot = { state: 'pending', blockedReason: null };
  private inFlight: Promise<AgeAssuranceGateState> | null = null;
  private listeners = new Set<StateListener>();

  getState = (): AgeAssuranceGateState => this.snapshot.state;
  getSnapshot = (): AgeAssuranceSnapshot => this.snapshot;

  subscribe = (listener: StateListener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  start = (options: StartupOptions): Promise<AgeAssuranceGateState> => {
    if (this.snapshot.state !== 'pending' || this.inFlight) {
      return this.inFlight ?? Promise.resolve(this.snapshot.state);
    }

    if (options.isWeb) {
      return this.completeBypass(options, 'web_bypass', 'startup');
    }

    if (!options.enabled) {
      return this.completeBypass(options, 'flag_disabled', 'startup');
    }

    return this.runAttempt(options, 'startup');
  };

  retry = (options: RetryOptions): Promise<AgeAssuranceGateState> => {
    if (this.inFlight) {
      return this.inFlight;
    }

    if (this.snapshot.state !== 'blocked') {
      return Promise.resolve(this.snapshot.state);
    }

    const blockedReason = this.snapshot.blockedReason;
    this.setSnapshot({ state: 'pending', blockedReason: null });
    if (!options.enabled) {
      return this.completeBypass(options, 'flag_disabled', 'retry');
    }

    return this.runAttempt(options, 'retry', blockedReason);
  };

  resetForTesting = (): void => {
    this.snapshot = { state: 'pending', blockedReason: null };
    this.inFlight = null;
    this.listeners.clear();
  };

  private completeBypass = (
    dependencies: AgeAssuranceAttemptDependencies,
    outcome: Extract<AgeAssuranceOutcome, 'flag_disabled' | 'web_bypass'>,
    trigger: AgeAssuranceAttemptTrigger
  ): Promise<AgeAssuranceGateState> => {
    dependencies.logTerminalDecision({
      message: 'Age assurance decision completed',
      ageAssuranceOutcome: outcome,
      ageAssuranceTrigger: trigger,
      ageAssuranceNativeResult: 'not_called',
      ageAssuranceEligibility: 'not_checked',
      ageAssuranceGateState: 'allowed',
      ageAssuranceBlockedReason: null,
    });
    this.setSnapshot({ state: 'allowed', blockedReason: null });
    return Promise.resolve('allowed');
  };

  private runAttempt = (
    dependencies: AgeAssuranceAttemptDependencies,
    trigger: AgeAssuranceAttemptTrigger,
    previousBlockedReason: AgeAssuranceBlockedReason | null = null
  ): Promise<AgeAssuranceGateState> => {
    const attempt = executeAgeAssuranceAttempt(dependencies)
      .then(result => {
        // A failed recheck cannot undo a known block. Only an explicit 13+ or
        // out-of-scope result clears it; off is handled by completeBypass.
        const canClearBlock =
          result.outcome === 'age_13_plus' ||
          result.outcome === 'outside_texas' ||
          result.outcome === 'ios_not_eligible';
        const snapshot: Exclude<AgeAssuranceSnapshot, { state: 'pending' }> =
          result.state === 'allowed' && previousBlockedReason && !canClearBlock
            ? { state: 'blocked', blockedReason: previousBlockedReason }
            : result.state === 'blocked'
            ? { state: 'blocked', blockedReason: result.blockedReason }
            : { state: 'allowed', blockedReason: null };
        dependencies.logTerminalDecision({
          message: 'Age assurance decision completed',
          ageAssuranceOutcome: result.outcome,
          ageAssuranceTrigger: trigger,
          ageAssuranceNativeResult: result.nativeResult,
          ageAssuranceEligibility: result.eligibility,
          ageAssuranceGateState: snapshot.state,
          ageAssuranceBlockedReason: snapshot.blockedReason,
        });
        this.setSnapshot(snapshot);
        return snapshot.state;
      })
      .finally(() => {
        if (this.inFlight === attempt) {
          this.inFlight = null;
        }
      });

    this.inFlight = attempt;
    return attempt;
  };

  private setSnapshot = (snapshot: AgeAssuranceSnapshot): void => {
    this.snapshot = snapshot;
    this.listeners.forEach(listener => listener(snapshot));
  };
}

export const processAgeAssuranceCoordinator = new AgeAssuranceCoordinator();
